import React from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Driver } from '../../../data/models/driver.model';
import {
  GenericDocument,
  DriverDocument,
} from '../../../data/models/document.model';
import * as driverSlice from '../../../data/driver.slice';
import * as referenceSlice from '../../../data/reference.slice';
import { GenericDocumentType } from '../../../data/models/document-type.model';
import documentUtils from '../../../services/documentUtils';
import DocumentList from '../DocumentList';

interface DriverDocumentsProps {
  driver: Driver | null;
  uploadingDocument: boolean;
  readOnly: boolean;
  allowView: boolean;
}

export default function DriverDocuments({
  driver,
  uploadingDocument,
  readOnly,
  allowView,
}: DriverDocumentsProps) {
  const dispatch = useAppDispatch();

  const driverDocuments = useAppSelector(driverSlice.selectDriverDocuments);
  const driverDocumentTypes = useAppSelector(
    referenceSlice.selectDriverDocumentTypes,
  );

  const [documents, setDocuments] = React.useState<GenericDocument[]>([]);
  const [documentTypes, setDocumentTypes] = React.useState<
    GenericDocumentType[]
  >([]);

  React.useEffect(() => {
    if (driver && driver.driverID) {
      dispatch(driverSlice.fetchDriverDocumentsAsync(driver.driverID));
    }
  }, [driver, dispatch]);

  React.useEffect(() => {
    if (driver && driverDocuments && driverDocumentTypes) {
      const docTypes =
        documentUtils.driverTypesToGenericTypes(driverDocumentTypes);
      const docs = documentUtils.driverToGeneric(
        driverDocuments,
        driverDocumentTypes,
      );
      setDocuments(docs);
      setDocumentTypes(docTypes);
    }
  }, [driver, driverDocuments, driverDocumentTypes]);

  const saveDriverDocument = (
    file: any,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    if (driver && driver.driverID) {
      const doc: DriverDocument = {
        driverDocumentTypeID: typeId,
        driverDocumentState: 'active',
        signedOnDate: signedOn || null,
        effectiveStartDate: effectiveStart || null,
        effectiveEndDate: effectiveEnd || null,
      };
      dispatch(
        driverSlice.uploadDriverDocumentAsync(driver.driverID, file, doc),
      );
    }
  };

  const deleteDriverDocument = (id: number) => {
    if (driver && driver.driverID) {
      dispatch(driverSlice.deleteDriverDocumentAsync(driver.driverID, id));
    }
  };

  const updateDriverDocument = (
    documentId: number,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    if (driver && driver.driverID) {
      const doc: DriverDocument = {
        driverDocumentID: documentId,
        driverDocumentTypeID: typeId,
        driverDocumentState: 'active',
        signedOnDate: signedOn || null,
        effectiveStartDate: effectiveStart || null,
        effectiveEndDate: effectiveEnd || null,
      };
      dispatch(driverSlice.updateDriverDocumentAsync(driver.driverID, doc));
    }
  };

  return (
    <DocumentList
      providerID={driver!.transportationProviderID}
      documents={documents}
      documentTypes={documentTypes}
      uploadingDocument={uploadingDocument}
      readOnly={readOnly}
      allowView={allowView}
      save={saveDriverDocument}
      update={updateDriverDocument}
      doDelete={deleteDriverDocument}
    />
  );
}
